import stringWidth from 'string-width';
import { defaultRtn, DirectionType, RtnType } from '../declareTypes';

/** 字符处理 */
const stringHandle = async () => { };

/**
* 返回字符串首字母大写
* @param source 原字符串
* @returns 新字符串
*/
stringHandle.firstUpperCase = (source: string) => {
  return source[0].toLocaleUpperCase().concat(source.slice(1));
};

/**
 * 按显示宽度截断字符串
 * @param str 
 * @param targetWidth 
 * @returns 
 */
const truncateToDisplayWidth = (str: string, targetWidth: number): string => {
  let currentWidth = 0;
  let result = [];
  for (const char of str) {
    const charWidth = stringWidth(char);
    if (currentWidth + charWidth > targetWidth) break;
    currentWidth += charWidth;
    result.push(char);
  }
  return result.join('');
};

/**
 * 通用填充（解决中文长度问题），代替padEnd、padStart
 * @param str 字符串
 * @param targetWidth 目标长度
 * @param options 选项
 * @returns 
 */
stringHandle.padByDisplayWidth = (
  str: string,
  targetWidth: number,
  options?: {
    /** 填充字符，默认: ' ' */
    fillChar?: string,
    /** 位置，默认: 'START' */
    direction?: DirectionType
  }
): RtnType => {
  const rtn = defaultRtn();
  try {
    let { fillChar = ' ', direction = 'START' } = options || {};
    // string-width V5.0.0是以ESM格式发布，
    // 在CommonJS的文件里用import stringWidth from 'string-width'会导致问题。
    // 改用动态 import()
    // const stringWidth = await import('string-width').then(m => m.default);
    // 降为^4.2.3使用
    const getDisplayWidth = (s: string) => stringWidth(s);
    const currentWidth = getDisplayWidth(str);
    if (currentWidth >= targetWidth) {
      rtn.data = str;
      rtn.success = true;
      return rtn;
    }
    const fillCharWidth = getDisplayWidth(fillChar);
    if (fillCharWidth > 0) {
      const paddingNeeded = targetWidth - currentWidth;
      const fillCount = Math.floor(paddingNeeded / fillCharWidth);
      if (fillCount > 0) {
        let paddedStr = str;
        switch (direction) {
          case 'START': {
            const padding = fillChar.repeat(fillCount);
            paddedStr = padding + str;
            // 截断左边填充，保留右侧目标宽度
            const totalWidth = currentWidth + fillCount * fillCharWidth;
            if (totalWidth > targetWidth) {
              const overflow = totalWidth - targetWidth;
              const overflowChars = Math.ceil(overflow / fillCharWidth);
              paddedStr = paddedStr.slice(overflowChars);
            }
            break;
          }
          case 'END': {
            const padding = fillChar.repeat(fillCount);
            paddedStr = str + padding;
            // 截断右侧填充，保留左侧目标宽度
            const totalWidth = currentWidth + fillCount * fillCharWidth;
            if (totalWidth > targetWidth) {
              const overflow = totalWidth - targetWidth;
              const overflowChars = Math.ceil(overflow / fillCharWidth);
              paddedStr = paddedStr.slice(0, -overflowChars);
            }
            break;
          }
          case 'CENTER': {
            const leftFill = Math.floor(fillCount / 2);
            const rightFill = fillCount - leftFill;
            const leftPadding = fillChar.repeat(leftFill);
            const rightPadding = fillChar.repeat(rightFill);
            paddedStr = leftPadding + str + rightPadding;
            // 计算总填充宽度并调整可能的溢出
            const totalPaddingWidth = (leftFill + rightFill) * fillCharWidth;
            let totalWidth = currentWidth + totalPaddingWidth;
            if (totalWidth > targetWidth) {
              const overflow = totalWidth - targetWidth;
              const overflowPerSide = Math.ceil(overflow / 2);
              // 分别从左右截断溢出的部分
              let leftOverflow = 0, rightOverflow = 0;
              leftOverflow = Math.min(Math.ceil(overflowPerSide / fillCharWidth), leftFill);
              rightOverflow = Math.ceil((overflow - leftOverflow * fillCharWidth) / fillCharWidth);
              paddedStr = paddedStr
                .slice(leftOverflow)
                .slice(0, rightOverflow > 0 ? -rightOverflow : undefined);
            }
            break;
          }
        }
        // 最终确保显示宽度不超过目标
        rtn.data = truncateToDisplayWidth(paddedStr, targetWidth);
      } else {
        rtn.data = str;
      }
      rtn.success = true;
    } else {
      rtn.message = '填充字符的显示宽度必须大于0';
    }
  } catch (error: any) {
    rtn.message = '错误！';
    if (error) {
      const { message } = error;
      rtn.message = `错误！${message}`;
    }
  }
  return rtn;
};

/**
 * 比较两字符串
 * @param a 字符串一
 * @param b 字符串二
 * @returns rtnType
 */
stringHandle.compareStrings = (a: string, b: string): RtnType => {
  const rtn = defaultRtn();
  type DiffResult = {
    index: number;
    charA: string | undefined;
    charB: string | undefined;
  };
  const compareStrings = (a: string, b: string): DiffResult[] => {
    const diffs: DiffResult[] = [];
    const maxLength = Math.max(a.length, b.length);
    for (let i = 0; i < maxLength; i++) {
      const charA = a[i];
      const charB = b[i];
      if (charA !== charB) {
        diffs.push({ index: i, charA, charB });
      }
    }
    return diffs;
  };
  const formatDiff = (diffs: DiffResult[]): string => {
    if (diffs.length === 0) return "Strings are identical";
    return diffs
      .map(d => {
        const charA = d.charA ?? "[none]";
        const charB = d.charB ?? "[none]";
        return `Index ${d.index}: '${charA}' → '${charB}'`;
      })
      .join("\n");
  }
  rtn.success = true;
  rtn.data = formatDiff(compareStrings(a, b));
  return rtn;
};

/**
 * 从字符串模板中提取变量并生成JSON字符串
 * @param template 字符串
 * @returns 
 * @example stringHandle.extractVariablesToJson(`尊敬的\${appellationName}：您好！您在 \${branchStoreName} 预订用餐位置安排如下： 
[时间]：\${timeTypeName}（恭候）；
[\${roomTypeName}]（订）：\${roomName}；
[地址]：\${address}；
[服务电话]：\${phoneNumber}；
[停车]建议停\${parkingLot}；
祝您用餐愉快！`)
return data: {
  "appellationName": "",
  "branchStoreName": "",
  "timeTypeName": "",
  "roomTypeName": "",
  "roomName": "",
  "address": "",
  "phoneNumber": "",
  "parkingLot": ""
}
 */
stringHandle.extractVariablesToJson = (template: string): RtnType => {
  const rtn = defaultRtn();
  try {
    // 正则表达式匹配 ${...} 格式的变量
    const regex = /\$\{([^}]+)\}/g;
    const variables = new Set<string>();
    let match;
    // 循环匹配所有变量并存入 Set（自动去重）
    while ((match = regex.exec(template)) !== null) {
      variables.add(match[1]);
    }
    // 创建 JSON 对象
    const resultObj: Record<string, string> = {};
    variables.forEach(variable => {
      resultObj[variable] = "";
    });
    // 返回格式化的 JSON 字符串
    rtn.data = JSON.stringify(resultObj, null, 2);
    rtn.success = true;
  }
  catch (error: any) {
    rtn.message = '错误！';
    if (error) {
      const { message } = error;
      rtn.message = `错误！${message}`;
    }
  }
  return rtn;
};

/**
 * url转apiId 和 apiExtend
 * @param url http地址
 * @returns 
 */
stringHandle.urlToApiIdAndApiExtend = (url: string) => {
  const rtn = defaultRtn();
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(segment => segment !== '');
    // 提取最后两个参数
    const apiId = pathSegments[pathSegments.length - 2];
    const apiExtend = pathSegments[pathSegments.length - 1];
    const basePath = pathSegments.slice(0, -2).join('/');
    const baseUrl = `${urlObj.origin}/${basePath}`;
    // 解析URL的查询参数（标准方法，处理?后的部分）
    const searchParams = new URLSearchParams(urlObj.search);
    const paramsObj: {
      [key: string]: string | undefined;
    } = {};
    for (const [key, value] of searchParams) {
      paramsObj[key] = value;
    }
    // 如果标准查询参数为空，但URL中包含"&"或"="，尝试从hash或路径末尾解析（非标准容错）
    if (Object.keys(paramsObj).length === 0 && (url.includes('&') || url.includes('='))) {
      // 尝试从hash部分解析
      if (urlObj.hash.includes('=')) {
        const hashParams = new URLSearchParams(urlObj.hash.split('?')[1] || '');
        for (const [key, value] of hashParams) {
          paramsObj[key] = value;
        }
      }
      // 如果仍然为空，尝试从整个URL中手动解析（最后的手段）
      if (Object.keys(paramsObj).length === 0) {
        const manualParams = url.split('?')[1] || url.split('&')[0]; // 简单分割
        if (manualParams) {
          const pairs = manualParams.split('&');
          for (const pair of pairs) {
            const [key, value] = pair.split('=');
            if (key && value) {
              paramsObj[decodeURIComponent(key)] = decodeURIComponent(value);
            }
          }
        }
      }
    }
    rtn.data = {
      /** apiId */
      apiId,
      /** apiExtend */
      apiExtend,
      /** 基本地址 */
      baseUrl,
      /** query参数 */
      params: paramsObj
    }
    rtn.success = true;
  }
  catch (error: any) {
    rtn.message = '错误！';
    if (error) {
      const { message } = error;
      rtn.message = `错误！${message}`;
    }
  }
  return rtn;
};


/**
 * 将Blob对象转换为Base64字符串
 * @param blob 需要转换的Blob对象
 * @returns 返回一个Promise，解析为Base64字符串
 */
stringHandle.blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result 格式为 "data:[MIME类型];base64,实际Base64字符串"
      const dataURL = reader.result as string;
      // 分割字符串，取Base64部分（逗号后的内容）
      const base64 = dataURL.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    // 开始读取Blob内容，结果将格式化为Data URL（包含Base64编码）
    reader.readAsDataURL(blob);
  });
};

/**
 * 替换模板中的占位符
 * @param template 字符串
 * @param replacements 占位符列表，例如：{
        typeName: typeName,
        membershipName: membershipName
    }
 * @param options 选项
 */
stringHandle.replaceTemplate = (template: string,
  replacements: Record<string, string | number | boolean | Object>,
  options?: {
    prefix?: string
  }) => {
  const rtn = defaultRtn();
  try {
    const { prefix = '@' } = options || {};
    const escapeRegExp = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let result = template;
    for (const [key, value] of Object.entries(replacements)) {
      const escapedPrefix = escapeRegExp(prefix);
      const escapedKey = escapeRegExp(key);
      const regex = new RegExp(`${escapedPrefix}${escapedKey}`, 'g');
      result = result.replace(regex, () => String(value));  // 关键修改
    }
    rtn.data = result;
    rtn.success = true;
  }
  catch (error: any) {
    rtn.message = '错误！';
    if (error) {
      const { message } = error;
      rtn.message = `错误！${message}`;
    }
  }
  return rtn;
};


/**
 * 将非标准 JSON 字符串（如 `{name: "李生", age: 18}`）解析为 JS 对象。
 * 自动处理：
 * - 键名无引号
 * - 值使用单引号或双引号
 * - 数字、布尔值、null
 * - 简单嵌套对象/数组
 *
 * ⚠️ 局限性：
 * - 不支持键名含特殊字符（如空格、连字符），若需支持请使用 JSON5 库
 * - 不支持注释、尾逗号
 */
stringHandle.parseObjectString = <T = Record<string, string | number | boolean | Object>>(str: string): RtnType<T> => {
  const rtn = defaultRtn<T>();
  try {
    // 1. 统一将单引号替换为双引号（避免后续解析失败）
    let normalized = str.replace(/'/g, '"');
    // 2. 给未加引号的键名加上双引号
    //    匹配规则：前面是 { 或 ,（允许空白），后面跟合法的 JS 标识符，然后是冒号
    normalized = normalized.replace(
      /([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g,
      '$1"$2":'
    );
    rtn.data = JSON.parse(normalized);
    rtn.success = true;
  }
  catch (error: any) {
    rtn.message = '错误！';
    if (error) {
      const { message } = error;
      rtn.message = `错误！${message}`;
    }
  }
  return rtn;
};

export default stringHandle;